import React, { useEffect, useState, useMemo } from 'react'
import clsx from 'clsx'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'

import * as AppConstants from '../../utils/constants'
import { setFlashMessage } from '../../actions/flashActions'
import { paymentSuccess } from '../../actions/userActions'
import { sessionClear } from '../../actions/sessionActions'

import StripeWrapper from './Stripe'
import PayPal from './PayPal'
import StripeButton from './StripeButton'

import Card from '../utils/Card'
import Label from '../utils/Label'
import Input from '../utils/Input'
import Upgrade from './Upgrade'
import Thanks from './Thanks'
import Sms from './Sms'
import Renew from './Renew'

import UpgradeQ from './UpgradeQ'

const PricingSegment = ({ isThanks, className, children }) => (
  <div className={clsx(className, 'w-full mb-12', !isThanks && 'shadow-md border rounded-lg p-4')}>{children}</div>
)
const Payment = ({ match }) => {
  const productParam = match && match.params.product

  const dispatch = useDispatch()
  const user = useSelector(store => store.user.user)
  const premium = useSelector(store => store.user.premium)

  const [emailValue, setEmailValue] = useState('')
  const [nameValue, setNameValue] = useState('')

  const [amountValue, setAmountValue] = useState(30)
  const [loaded, setLoaded] = useState(false)
  const [emailError, setEmailError] = useState(false)
  const [label, setLabel] = useState('PayPal')

  const [productSelected, setProductSelected] = useState(null)

  useEffect(() => {
    setEmailValue(user.email)
    setNameValue(user.name)
  }, [user])

  const defaultProduct = useMemo(() => (premium ? 'thanks' : 'upgrade'), [premium])
  const activeProduct = useMemo(
    () => productSelected || productParam || defaultProduct,
    [productSelected, productParam, defaultProduct]
  )
  const product = useMemo(() => {
    return (
      AppConstants.PRODUCTS[activeProduct] && {
        key: activeProduct,
        ...AppConstants.PRODUCTS[activeProduct]
      }
    )
  }, [activeProduct])

  const isThanks = useMemo(() => productSelected || product.key === 'thanks', [productSelected, product])

  useEffect(() => {
    if (productParam && !product) {
      dispatch(push('/premium/thanks'))
    }
    product && setAmountValue(product.value)
    product && setLoaded(true)
  }, [productParam, product])

  const onPaymentSuccess = (provider, response) => {
    dispatch(
      paymentSuccess({
        provider,
        response,
        product: product.key,
        name: nameValue,
        email: emailValue,
        user: user?.id,
        userEmail: user?.email,
        userName: user?.name
      })
    )
    dispatch(push('/settings'))
    dispatch(setFlashMessage(AppConstants.PAYMENTS.success, 'success'))
  }

  const handleAmountChange = amount => {
    setAmountValue(amount)
  }

  const handleValidation = () => {
    const isValid = emailValue && emailValue.length > 0
    setEmailError(!isValid)
    return isValid
  }

  return (
    <div className="md:flex mb-12 space-x-0 md:space-x-12">
      <div className="max-w-md w-full flex-grow">
        <PricingSegment isThanks={isThanks}>
          {isThanks && (
            <Thanks
              onChange={handleAmountChange}
              defaultValue={product.value}
              product={product}
              setProductSelected={setProductSelected}
            />
          )}

          {!productSelected && product.key === 'upgrade' && <Upgrade />}
          {!productSelected && product.key === 'renew' && <Renew />}
          {!productSelected && product.key === 'sms' && <Sms onChange={handleAmountChange} />}
        </PricingSegment>


        <div className="flex justify-between mb-8">
          <Label>Payment Total:</Label>
          <div className="text-right text-2xl">${amountValue}.00</div>
        </div>

        <Label>Email Address:</Label>

        {emailError && (
          <Card color="lightOrange" small className="inline-block mt-1">
            <span className="text-lg font-bold">Email Address is Required</span>
          </Card>
        )}
        <div className="flex mb-8">
          <Input
            onChange={e => !user.id && setEmailValue(e.target.value)}
            defaultValue={emailValue}
            disabled={!!user.id}
            trailingButton={!!user.id}
          />

          {user.id && (
            <button
              onClick={() => dispatch(sessionClear())}
              className="flex-none border rounded-r px-4 bg-gray-200 border-gray-300 text-gray-500"
            >
              <span className="text-lg">Clear</span>
            </button>
          )}
        </div>

        <Label>Name:</Label>
        <Input onChange={e => setNameValue(e.target.value)} defaultValue={nameValue} />

        <div className="flex mb-8"></div>

        <div className="">
          <Label className="mb-4">{label}</Label>
          {loaded && (
            <PayPal
              amount={amountValue}
              name={nameValue}
              email={emailValue}
              onSuccess={onPaymentSuccess}
              validation={handleValidation}
              product={product}
            />
          )}

          <StripeButton
            amount={amountValue}
            name={nameValue}
            email={emailValue}
            onSuccess={onPaymentSuccess}
            validation={handleValidation}
            product={product}
            onButtonAdd={() => setLabel('One Click Options')}
          />
        </div>

        <div className="flex items-center space-x-4 mt-4">
          <div className="flex-grow border-t-2 border-gray-400"></div>
          <div className="text-4xl text-gray-400">OR</div>
          <div className="flex-grow border-t-2 border-gray-400"></div>
        </div>

        <StripeWrapper
          amount={amountValue}
          name={nameValue}
          email={emailValue}
          onSuccess={onPaymentSuccess}
          validation={handleValidation}
          product={product}
        />
      </div>
      <div className="md:max-w-sm lg:max-w-lg prose mt-12 md:mt-0">
        <UpgradeQ />
      </div>
    </div>
  )
}

export default Payment
